// Stripe Connect — UI controller for the "Stripe Connect" card in
// coach settings → Payments tab.
//
// Responsibilities:
//   - On settings load: fetch status and render the single live row
//   - Handle "Connect live Stripe" button (opens OAuth URL in same window)
//   - Handle "Disconnect" button (confirmation modal → POST)
//   - On return from OAuth: detect ?stripeConnect=connected/error/cancelled
//     query string and flash a message
//
// Coaches only ever operate in live mode. Test-mode Connect exists in
// the backend schema for platform/internal use but is not surfaced in
// the coach UI.
//
// Below the connected row: the coach's own Stripe key for subscriptions made
// outside FitOps Pro (stripe-owner-key.js on the server, the window in
// stripe-key-dialog.js). Shown once Stripe is connected, to whoever can
// manage billing.

import { askForStripeKey } from './stripe-key-dialog.js';
import { showConfirm } from './utils.js';

const FN_BASE = (typeof window !== 'undefined' && window.location?.origin)
  ? `${window.location.origin}/.netlify/functions`
  : 'https://app.fitopspro.com/.netlify/functions';

let _supabase = null;
let _loaded = false;

export function initStripeConnect(supabase) {
  _supabase = supabase;

  const liveRow = document.getElementById('stripe-connect-row-live');
  if (!liveRow) return;

  if (!_loaded) {
    _loaded = true;
    loadStatus();
    maybeShowReturnFlash();
  }
}

async function authToken() {
  const { data: { session } } = await _supabase.auth.getSession();
  return session?.access_token || null;
}

async function loadStatus() {
  const loading = document.getElementById('stripe-connect-loading');
  const liveRow = document.getElementById('stripe-connect-row-live');
  if (!loading || !liveRow) return;

  try {
    const token = await authToken();
    if (!token) return;
    const res = await fetch(`${FN_BASE}/stripe-connect-status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    loading.classList.add('hidden');
    liveRow.style.display = 'block';

    renderLiveRow(liveRow, data.live || { connected: false });
  } catch (err) {
    loading.textContent = `Couldn't check status: ${err.message}`;
  }
}

function renderLiveRow(row, status) {
  const statusEl = row.querySelector('.stripe-connect-row-status');
  const acctEl = row.querySelector('.stripe-connect-row-acct');
  const incompleteEl = row.querySelector('.stripe-connect-row-incomplete');
  const actionsEl = row.querySelector('.stripe-connect-row-actions');

  if (!status.connected) {
    document.getElementById('stripe-owner-key-row')?.classList.add('hidden');
    if (statusEl) statusEl.textContent = 'Not connected';
    if (acctEl) acctEl.textContent = '';
    if (incompleteEl) incompleteEl.classList.add('hidden');
    if (actionsEl) {
      actionsEl.innerHTML = `
        <button class="btn-primary" data-action="connect">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px;margin-right:6px;"><path d="M13.5 2L3 14h7l-1 8 10.5-12h-7z"/></svg>
          Connect Stripe
        </button>
      `;
      wireRowActions(actionsEl);
    }
    return;
  }

  // Connected
  const ready = status.chargesEnabled && status.payoutsEnabled && status.detailsSubmitted;
  if (statusEl) {
    statusEl.innerHTML = `
      <span style="display:inline-flex;align-items:center;gap:6px;color:var(--success,#10b981);font-weight:600;">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
        Connected
      </span>
    `;
  }
  if (acctEl) acctEl.textContent = status.accountId || '';
  if (incompleteEl) incompleteEl.classList.toggle('hidden', !!ready);
  if (actionsEl) {
    actionsEl.innerHTML = `
      <a href="https://dashboard.stripe.com" target="_blank" rel="noopener" class="btn-secondary" style="text-decoration:none;">Open Stripe Dashboard</a>
      <button class="btn-secondary" data-action="disconnect" style="color:var(--danger,#ef4444);border-color:var(--danger,#ef4444);">Disconnect</button>
    `;
    wireRowActions(actionsEl);
  }
  loadOwnerKeyRow();
}

// ── The coach's own Stripe key ───────────────────────────────────────
async function ownerKeyRequest(method, body) {
  const token = await authToken();
  if (!token) throw new Error('Not signed in');
  const res = await fetch(`${FN_BASE}/stripe-owner-key`, {
    method,
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.error || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return data;
}

async function loadOwnerKeyRow() {
  const row = document.getElementById('stripe-owner-key-row');
  if (!row) return;
  let state;
  try {
    state = (await ownerKeyRequest('GET')).live;
  } catch {
    // No billing permission, or the read failed: leave the row out rather
    // than offer buttons that cannot work.
    row.classList.add('hidden');
    return;
  }
  const saved = !!(state && state.saved);
  const statusEl = row.querySelector('.stripe-owner-key-status');
  const actionsEl = row.querySelector('.stripe-owner-key-actions');
  if (statusEl) {
    statusEl.textContent = saved
      ? `Key ending in ${state.last4}, saved ${new Date(state.savedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}. FitOps Pro uses it only when Stripe won't let it change a subscription or invoice that another app made, like GoHighLevel.`
      : "Clients brought over from another app, like GoHighLevel, may be on subscriptions and invoices FitOps Pro can't change. Add a key from your Stripe account so it can.";
  }
  if (actionsEl) {
    actionsEl.innerHTML = saved
      ? `<button class="btn-secondary" data-key-action="replace">Replace key</button>
         <button class="btn-secondary" data-key-action="remove" style="color:var(--danger,#ef4444);border-color:var(--danger,#ef4444);">Remove key</button>`
      : '<button class="btn-secondary" data-key-action="add">Add Stripe key</button>';
    actionsEl.querySelectorAll('[data-key-action]').forEach((btn) => {
      btn.addEventListener('click', () => onOwnerKeyAction(btn.dataset.keyAction, btn));
    });
  }
  row.classList.remove('hidden');
}

async function onOwnerKeyAction(action, btn) {
  if (action === 'remove') {
    const ok = await showConfirm(
      "Remove your Stripe key? FitOps Pro won't be able to cancel or change subscriptions that another app made, like GoHighLevel, until you add one again.",
      { okLabel: 'Remove key', danger: true },
    );
    if (!ok) return;
    btn.disabled = true;
    try {
      await ownerKeyRequest('DELETE', { mode: 'live' });
      flashSuccess('Stripe key removed.');
    } catch (err) {
      flashError(`Could not remove the key: ${err.message}`);
    }
    loadOwnerKeyRow();
    return;
  }
  const saved = await askForStripeKey({
    replacing: action === 'replace',
    mode: 'live',
    save: (key) => ownerKeyRequest('POST', { key, mode: 'live' }),
  });
  if (saved) {
    flashSuccess('Stripe key saved.');
    loadOwnerKeyRow();
  }
}

function wireRowActions(actionsEl) {
  actionsEl.querySelectorAll('[data-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.dataset.action === 'connect') handleConnectClick(btn);
      else if (btn.dataset.action === 'disconnect') handleDisconnectClick(btn);
    });
  });
}

async function handleConnectClick(btn) {
  btn.disabled = true;
  const originalText = btn.innerHTML;
  btn.innerHTML = 'Redirecting…';

  try {
    const token = await authToken();
    if (!token) throw new Error('Not signed in');

    const res = await fetch(`${FN_BASE}/stripe-connect-authorize?mode=live`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${res.status}`);
    }
    const { url } = await res.json();
    window.location.href = url;
  } catch (err) {
    btn.disabled = false;
    btn.innerHTML = originalText;
    flashError(`Couldn't start Stripe connection: ${err.message}`);
  }
}

async function handleDisconnectClick(btn) {
  const proceed = await confirmDisconnect();
  if (!proceed) return;

  btn.disabled = true;
  const originalText = btn.textContent;
  btn.textContent = 'Disconnecting…';

  try {
    const token = await authToken();
    if (!token) throw new Error('Not signed in');
    const res = await fetch(`${FN_BASE}/stripe-connect-disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ confirmation: 'DISCONNECT', mode: 'live' }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      // Disconnected, with a half that did not land here (its words say which): not a failed disconnect. Its words name
      // what the coach must still do in their own Stripe, and no later press says them again: they stay (the fix check)
      if (errBody.disconnected) {
        flashError(errBody.error, { keep: true });
        loadStatus();
        return;
      }
      // Stopped after card payments were already cancelled in Stripe: the next press can't say so, so these words stay
      if (Number(errBody.cancelledCardPayments) > 0) {
        btn.disabled = false;
        btn.textContent = originalText;
        flashError(`Disconnect failed: ${errBody.error}`, { keep: true });
        return;
      }
      throw new Error(errBody.error || `HTTP ${res.status}`);
    }
    const result = await res.json();
    // ⚠️ WHAT THE COACH STILL HAS TO DO STAYS (the fix check, finding 22): the card payments to delete in their own
    // Stripe and the programs to refund are named only in this answer (the account is let go, so a later press finds
    // nothing), so they stay until the coach closes them, never in a green flash that goes in 6 seconds
    const toDo = !!(result.cardPaymentsWords || result.paidProgramsWords || (result.cardPaymentsNotReached || []).length);
    if (toDo) flashToKeep(disconnectedWords(result));
    else flashSuccess(disconnectedWords(result));

    loadStatus();
  } catch (err) {
    btn.disabled = false;
    btn.textContent = originalText;
    flashError(`Disconnect failed: ${err.message}`);
  }
}

/**
 * What a finished disconnect says it did. Exported for its test.
 * @param {any} result the server's answer
 */
export function disconnectedWords(result) {
  const n = (result.cancelledSubscriptions || []).length;
  // Monthly payments set up to start later (Change billing, continuity set at sign-up) are cancelled too
  const w = (result.cancelledSchedules || []).length;
  // Installments step 4 (Task A15): so are the card installments still to come
  const c = Number(result.cancelledCardPayments) || 0;
  const parts = [
    n > 0 ? `${n} client subscription${n === 1 ? '' : 's'} cancelled` : '',
    w > 0 ? `${w} monthly payment${w === 1 ? '' : 's'} waiting to start cancelled` : '',
    c > 0 ? `${c} card payment${c === 1 ? '' : 's'} still to come cancelled` : '',
  ].filter(Boolean);
  const msg = parts.length ? `Stripe disconnected. ${parts.join(', ')}.` : `Stripe disconnected.`;
  // The server's own sentences for what the coach still has to do: card payments still owed in their own Stripe, and
  // programs paid ahead that will now not start (sent since 4c part 3, never shown until now)
  return [msg, result.cardPaymentsWords, result.paidProgramsWords].filter(Boolean).join(' ');
}

function confirmDisconnect() {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:10000;
      display:flex;align-items:center;justify-content:center;padding:20px;`;
    overlay.innerHTML = `
      <div style="background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:24px;max-width:520px;width:100%;">
        <h3 style="margin:0 0 12px 0;font-size:1rem;font-weight:700;color:var(--danger,#ef4444);">Disconnect Stripe?</h3>
        <p style="margin:0 0 12px 0;font-size:0.9rem;line-height:1.6;color:var(--text);">
          This cancels <strong>every active client subscription</strong> on your Stripe account
          before disconnecting — otherwise clients would keep being billed with no way for us to manage it.
        </p>
        <p style="margin:0 0 16px 0;font-size:0.9rem;line-height:1.6;color:var(--text);">
          This cannot be undone. To proceed, type <code style="background:var(--surface-2,var(--bg));padding:1px 6px;border-radius:4px;border:1px solid var(--border);font-size:0.85em;">DISCONNECT</code> below.
        </p>
        <input id="cl-disconnect-input" type="text" placeholder="Type DISCONNECT here"
          style="width:100%;padding:10px 12px;background:var(--surface-2,var(--bg));border:1px solid var(--border);border-radius:6px;color:var(--text);font-size:0.9rem;margin-bottom:16px;"
          autocomplete="off" spellcheck="false" />
        <div style="display:flex;justify-content:flex-end;gap:8px;">
          <button id="cl-disconnect-cancel" class="btn-secondary">Cancel</button>
          <button id="cl-disconnect-confirm" class="btn-primary" style="background:var(--danger,#ef4444);border-color:var(--danger,#ef4444);" disabled>Yes, disconnect</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const input = overlay.querySelector('#cl-disconnect-input');
    const confirmBtn = overlay.querySelector('#cl-disconnect-confirm');
    const cancelBtn = overlay.querySelector('#cl-disconnect-cancel');

    input.focus();
    input.addEventListener('input', () => {
      confirmBtn.disabled = input.value.trim() !== 'DISCONNECT';
    });

    const cleanup = (result) => {
      document.body.removeChild(overlay);
      resolve(result);
    };
    confirmBtn.addEventListener('click', () => cleanup(true));
    cancelBtn.addEventListener('click', () => cleanup(false));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
  });
}

/**
 * The card's message box. A message the coach must act on (`keep`) stays until they press Close; any other goes after
 * 6 seconds. 'todo' is amber (what is left to do in their own Stripe), never success green.
 * @param {string} message @param {'success'|'error'|'todo'} type @param {{ keep?: boolean }} [o]
 */
function flash(message, type, { keep = false } = {}) {
  const el = document.getElementById('stripe-connect-flash');
  if (!el) return;
  const [bg, line, ink] = type === 'error' ? ['rgba(239,68,68,0.08)', 'rgba(239,68,68,0.3)', '#fca5a5']
    : type === 'todo' ? ['rgba(245,158,11,0.08)', 'rgba(245,158,11,0.35)', '#f59e0b']
      : ['rgba(16,185,129,0.08)', 'rgba(16,185,129,0.3)', '#86efac'];
  el.className = '';
  el.style.cssText = `
    margin-top:14px;padding:10px 12px;border-radius:6px;font-size:0.85rem;line-height:1.5;
    background:${bg};border:1px solid ${line};color:${ink};
    ${keep ? 'display:flex;gap:12px;align-items:flex-start;justify-content:space-between;' : ''}`;
  el.textContent = '';
  if (keep) {
    const words = document.createElement('span');
    words.style.cssText = 'flex:1;min-width:0;';
    words.textContent = message;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'btn-secondary';
    close.style.cssText = 'flex:none;padding:4px 10px;font-size:0.8rem;';
    close.textContent = 'Close';
    close.addEventListener('click', () => el.classList.add('hidden'));
    el.append(words, close);
  } else {
    el.textContent = message;
  }
  el.classList.remove('hidden');
  clearTimeout(flash._t);
  if (!keep) flash._t = setTimeout(() => el.classList.add('hidden'), 6000);
}
function flashSuccess(msg) { flash(msg, 'success'); }
/** @param {string} msg @param {{ keep?: boolean }} [o] */
function flashError(msg, o) { flash(msg, 'error', o); }
/** What the coach still has to do in their own Stripe: stays until Close, in amber. @param {string} msg */
function flashToKeep(msg) { flash(msg, 'todo', { keep: true }); }

function maybeShowReturnFlash() {
  let paramStr = null;
  try {
    paramStr = sessionStorage.getItem('stripeConnectReturn');
    if (paramStr) sessionStorage.removeItem('stripeConnectReturn');
  } catch {}
  if (!paramStr) paramStr = window.location.search;
  if (!paramStr) return;

  const params = new URLSearchParams(paramStr);
  const status = params.get('stripeConnect');
  if (!status) return;

  if (status === 'connected') {
    const acct = params.get('account');
    flashSuccess(`Stripe connected${acct ? ` (${acct})` : ''}.`);
  } else if (status === 'cancelled') {
    flashError(`Stripe connection was cancelled.`);
  } else if (status === 'error') {
    const reason = params.get('reason') || 'unknown';
    flashError(`Stripe connection failed: ${reason}`);
  }
}
